<template>
  <VcModal :title="title" modal-width="w-full max-w-[700px]" hide-actions is-mobile-fullscreen>
    <template #default="{ close }">
      <VcAddressForm
        :model-value="editableAddress"
        :countries="countriesWithoutEmbargoed"
        :disabled="loading"
        with-personal-info
        required-email
        required-city
        required-phone
        :default-billing-checkbox-hidden="props.defaultBillingCheckboxHidden"
        :default-shipping-checkbox-hidden="props.defaultShippingCheckboxHidden"
        @save="saveAddress"
      >
        <template #append="{ dirty, valid, values, setFieldValue }">
          <div v-if="currentCurrency.code === 'INR' && values.countryCode === 'IND'" class="mb-4">
            <VcInput
              :model-value="values.gstinTL ?? ''"
              :disabled="loading"
              :label="$t('pages.checkout.gstin')"
              :placeholder="$t('pages.checkout.gstin')"
              :maxlength="15"
              :error="!!getGstinWarning(values.gstinTL)"
              :aria-invalid="!!getGstinWarning(values.gstinTL)"
              :aria-describedby="getGstinWarning(values.gstinTL) ? gstinWarningId : undefined"
              autocomplete="off"
              @update:model-value="setFieldValue('gstinTL', $event ?? '')"
            />
            <p
              v-if="getGstinWarning(values.gstinTL)"
              :id="gstinWarningId"
              class="text-sm text-[--color-danger]"
              aria-live="polite"
            >
              {{ getGstinWarning(values.gstinTL) }}
            </p>
          </div>
          <div class="flex w-full flex-col gap-4">
            <VcButton
              color="primary"
              variant="solid"
              :disabled="!dirty || !valid"
              :loading="loading"
              type="submit"
              size="lg"
            >
              {{ $t("common.buttons.save_address") }}
            </VcButton>

            <VcButton class="p-4" color="secondary" variant="outline" size="lg" @click="close">
              {{ $t("common.buttons.cancel") }}
            </VcButton>
          </div>
        </template>
      </VcAddressForm>
    </template>
  </VcModal>
</template>

<script setup lang="ts">
import { clone } from "lodash";
import { computed, onMounted, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useCountriesWithoutEmbargoed } from "@/core/composables";
import { useCurrency } from "@/core/composables/useCurrency";
import { isValidGstin } from "@/shared/checkout/utils/gstin";
import { useComponentId } from "@/ui-kit/composables";
import type { MemberAddressTypeExtension } from "@/core/api/graphql/types";
import VcInput from "@/ui-kit/components/molecules/input/vc-input.vue";

interface IEmits {
  (event: "result", address: MemberAddressTypeExtension, isEdit: boolean): void;
}
interface IProps {
  address?: MemberAddressTypeExtension;
  defaultBillingCheckboxHidden?: boolean;
  defaultShippingCheckboxHidden?: boolean;
  loading?: boolean;
}

const emit = defineEmits<IEmits>();
const props = defineProps<IProps>();

const { countriesWithoutEmbargoed, loadCountriesWithoutEmbargoed } = useCountriesWithoutEmbargoed();
const { currentCurrency } = useCurrency();
const { t } = useI18n();
const gstinWarningId = useComponentId("gstin-warning");
const getGstinWarning = (value?: string | null) =>
  value && !isValidGstin(value) ? t("pages.checkout.invalid_gstin") : undefined;
const editableAddress = ref<MemberAddressTypeExtension>();
const title = computed<string>(() =>
  editableAddress?.value?.countryCode
    ? t("pages.account.addresses.edit_address_title")
    : t("common.titles.new_address"),
);
const isEdit = computed<boolean>(() => title.value.includes("Edit"));

onMounted(async () => {
  if (!countriesWithoutEmbargoed.value.length) {
    await loadCountriesWithoutEmbargoed();
  }
});

watchEffect(() => {
  editableAddress.value = clone(props.address);
});

function saveAddress(address: MemberAddressTypeExtension) {
  emit("result", address, isEdit.value);
}
</script>

<style lang="scss" scoped>
.vc-button {
  @apply py-2.5 #{!important};
}

.vc-button--outline--secondary {
  @apply border-[--color-neutral-a3] border-[1px] rounded-md #{!important};
}
</style>
